import './PendingSegmentationEdits.scss';
import { Dispatch } from 'react';
import { MdDelete, MdArrowForward } from 'react-icons/md';
import cn from 'classnames';
import { GroupedSegment } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { IconButton } from '@Components/IconButton';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { EditsMapAction, SegmentEditsMap, SegmentRow } from './segmentation-mapping-constants';
import { formatDate, isNewBillingSegmentValue } from './segmentationMappingUtils';

interface PendingSegmentationEditsProps {
  campaign_key: string;
  editsMap: SegmentEditsMap;
  dispatchEditsMap?: Dispatch<EditsMapAction>;
  segmentNames: string[];
  groupedSegments: Record<string, GroupedSegment[]> | undefined;
  onlyShowUnset?: boolean;
  hideUnchanged?: boolean;
}

export const PendingSegmentationEdits = ({
  campaign_key,
  editsMap,
  dispatchEditsMap,
  segmentNames,
  groupedSegments,
  onlyShowUnset = false,
  hideUnchanged = false
}: PendingSegmentationEditsProps) => {
  const row: SegmentRow = editsMap[campaign_key];
  const {
    account_name,
    account_id,
    campaign_name,
    campaign_id,
    ad_name,
    ad_id,
    ad_group_name,
    ad_group_id
  } = row;

  const account = account_name || account_id || '';
  const campaign = campaign_name || campaign_id || '';
  const adGroupData = ad_group_name || ad_group_id || '';
  const adData = ad_name || ad_id || '';

  const editTitle = (
    <div>
      <span className='field-label'>Account:</span> {account}
      {campaign && (
        <>
          <br />
          <span className='field-label'>Campaign:</span> {campaign}
        </>
      )}
      {adGroupData && (
        <>
          <br />
          <span className='field-label'>Ad Group:</span> {adGroupData}
        </>
      )}
      {adData && (
        <>
          <br />
          <span className='field-label'>Ad:</span> {adData}
        </>
      )}
    </div>
  );

  const trueGroupedSegmentNames = (groupName: string) => {
    if (!groupedSegments || !groupedSegments[groupName] || !row.edits) {
      return [];
    }
    return groupedSegments[groupName]
      .filter(gs => row.edits?.[gs.segmentName]?.label === 'Yes')
      .map(gs => gs.segmentName);
  };

  const falseGroupedSegmentNames = (groupName: string) => {
    if (!groupedSegments || !groupedSegments[groupName] || !row.edits) {
      return [];
    }
    return groupedSegments[groupName]
      .filter(gs => row.edits?.[gs.segmentName]?.label === 'No')
      .map(gs => gs.segmentName);
  };

  const hasGroupedSegments = groupedSegments && Object.keys(groupedSegments).length > 0;
  const groupedSegmentNames = hasGroupedSegments
    ? Object.values(groupedSegments).flatMap(gs => gs.map(g => g.segmentName))
    : [];
  const groupedSegmentTitles: string[] = [];
  if (hasGroupedSegments) {
    for (const groupName of Object.keys(groupedSegments)) {
      if (trueGroupedSegmentNames(groupName).length > 0) {
        groupedSegmentTitles.push(`${groupName}: Set to Yes`);
      }
      if (hideUnchanged && falseGroupedSegmentNames(groupName).length > 0) {
        groupedSegmentTitles.push(`${groupName}: Set to No`);
      }
    }
  }
  const filteredSegmentNames = hasGroupedSegments
    ? segmentNames
        .filter(segmentName => !groupedSegmentNames.some(s => s === segmentName))
        .concat(groupedSegmentTitles)
    : segmentNames;

  return (
    <div className='edit-container'>
      <div className='edit-title'>{editTitle}</div>
      <div className='edits-list'>
        {filteredSegmentNames.map(segmentName => {
          const editValue = row.edits?.[segmentName];
          const committedValue = row[segmentName];
          if (Array.isArray(editValue) || Array.isArray(committedValue)) {
            const committed = Array.isArray(committedValue) ? committedValue : [];
            const hasEdit = Array.isArray(editValue);
            const newEntries = hasEdit
              ? editValue.filter(entry => isNewBillingSegmentValue(entry, committed, segmentName))
              : [];
            const hasNewEntries = newEntries.length > 0;
            const isUnset = committed.length === 0 && !hasNewEntries;

            if ((hideUnchanged && !hasNewEntries) || (onlyShowUnset && !isUnset)) {
              return null;
            }

            return (
              <div className='edit wrap' key={segmentName}>
                <span className='segment-name aligned-label'>{segmentName}:</span>
                <div className='edit-values'>
                  {isUnset && <div className='old-value'>Not Set</div>}
                  {!isUnset &&
                    !hasNewEntries &&
                    committed.map((entry, i) => (
                      <div key={i} className='set-value'>
                        {entry[segmentName]}: {formatDate(entry.start_date)} –{' '}
                        {entry.end_date ? formatDate(entry.end_date) : 'No end date'}
                      </div>
                    ))}
                  {hasNewEntries && (
                    <>
                      {committed.length === 0 && (
                        <>
                          <span className='old-value'>Not Set</span>
                          <MdArrowForward />
                        </>
                      )}
                      {newEntries.map((entry, i) => (
                        <div key={i} className='new-value'>
                          {entry.label}: {formatDate(entry.startDate)} –{' '}
                          {entry.endDate ? formatDate(entry.endDate) : 'No end date'}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          }

          const newValue = row.edits?.[segmentName]?.label ?? null;
          const oldValue = row[segmentName];
          const valueIsChanged = Boolean(newValue) && oldValue !== newValue;

          // If there is no change in value, do not render
          if (newValue && newValue === oldValue) {
            return null;
          }

          // If we only want to show missing values, either a new value or an old value will suffice
          if (onlyShowUnset && (oldValue || newValue)) {
            return null;
          }

          if (segmentName.includes('Set to Yes')) {
            // hide if there are no edits and hideUnchanged is true
            const groupName = segmentName.split(': ')[0];
            const trueNames = trueGroupedSegmentNames(groupName);
            const falseNames = falseGroupedSegmentNames(groupName);
            const noGroupedEdits = trueNames.length === 0 && falseNames.length === 0;
            if (noGroupedEdits && hideUnchanged) {
              return null;
            }
            let displayValue: string;
            if (noGroupedEdits) {
              displayValue = 'Not Set';
            } else if (trueNames.length === 0) {
              displayValue = 'All Set to No';
            } else {
              displayValue = trueNames.join(', ');
            }
            return (
              <div className='edit wrap' key={segmentName}>
                <span className='segment-name aligned-label'>{segmentName}:</span>
                <span className={cn(!noGroupedEdits ? 'new-value' : 'old-value', 'aligned-value')}>
                  {displayValue}
                </span>
              </div>
            );
          }

          if (segmentName.includes('Set to No')) {
            const groupName = segmentName.split(': ')[0];
            return (
              <div className='edit wrap' key={segmentName}>
                <span className='segment-name aligned-label'>{segmentName}:</span>
                <span className='new-value aligned-value'>
                  {falseGroupedSegmentNames(groupName).join(', ')}
                </span>
              </div>
            );
          }

          // If there is no key in the edits map, then the value has not been altered and we might not want to show it
          if (hideUnchanged && !newValue) {
            return null;
          }

          return (
            <div className='edit' key={segmentName}>
              {newValue && dispatchEditsMap && (
                <IconButton
                  className='delete-button'
                  size={Size.SM}
                  appearance={Appearance.EMPTY}
                  design={Intent.ERROR}
                  onClick={() => {
                    dispatchEditsMap({
                      type: 'DELETE_SEGMENT_EDIT',
                      segmentNames: [segmentName],
                      campaign_keys: [campaign_key]
                    });
                  }}
                >
                  <MdDelete size={20} />
                </IconButton>
              )}
              {!newValue && dispatchEditsMap && <div className='delete-button-spacer' />}
              <span className='segment-name'>{segmentName}:</span>
              {valueIsChanged && (
                <>
                  <span className='old-value'>{oldValue || 'Not Set'}</span>
                  <MdArrowForward />
                </>
              )}
              <span
                className={cn({
                  'new-value': valueIsChanged,
                  'set-value': !valueIsChanged && Boolean(oldValue),
                  'old-value': !valueIsChanged && !oldValue
                })}
              >
                {(valueIsChanged ? newValue : oldValue) ?? 'Not Set'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
